'use client';

import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import {
  Button,
  Divider,
  IcCloseCircle,
  IconButton,
  Image,
  Padding,
  Typography,
} from '@musinsa/mds';
import { useDialog } from '@musinsa/mds-ui';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import type {
  GetCurationServiceResponse,
  GetCurationSnapsServiceResponse,
} from '@musinsa/services-curator';
import { updateCurationsService } from '@musinsa/services-curator';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Controller, useFormContext } from 'react-hook-form';
import { FloatingButtonGroup, ScrollToTopButton, useCuratorBroadcastPublisher } from '../common';
import { BottomButtonWrapper } from '../common/components/BottomButtonWrapper';
import { useCuratorSnackbar, useNavigate } from '../common/hooks';
import { curatorQueries } from '../query-factory';
import { LabeledTextField } from './LabeledTextField';
import type { ShopSnapCurationEditFormData } from './type';

interface Props {
  className?: string;
  curation: GetCurationServiceResponse;
  onClickSelectSnaps: () => void;
}

export function EditSnapCurationForm({ className, curation, onClickSelectSnaps }: Props) {
  const { service } = useDeviceInfo();
  const { data: userInfo } = useQuery(curatorQueries.profile.userInfo);
  const curatorId = userInfo?.snsTitle ?? '';
  const snackbar = useCuratorSnackbar();
  const dialog = useDialog();
  const { navigateBack } = useNavigate();

  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
    getValues,
  } = useFormContext<ShopSnapCurationEditFormData>();
  const { triggerQuerySync } = useCuratorBroadcastPublisher();

  const snapPoolQuery = useQuery<GetCurationSnapsServiceResponse>({
    ...curatorQueries.curations.snapDetail({ curationId: curation.curationId, size: 1 }),
  });

  const hasSnapPool = (snapPoolQuery.data?.list.length ?? 0) > 0;

  const musinsaBaseUrl = process.env.NEXT_PUBLIC_MUSINSA_BASE_URL ?? '';

  const { mutate: updateCuration } = useMutation({
    mutationFn: updateCurationsService,
    onSuccess: ({ curationId: id }) => {
      if (service === SERVICE.MUSINSA && curatorId) {
        amplitudeSendEvent({
          action_name: 'click',
          button_name: 'curation_edit_complete',
          component_name: 'button',
          event_name: 'click_button',
          from: getScreenName(),
          id: curatorId,
          name: 'curation_edit',
          section_name: 'curator_curationfeed',
          type: 'snap',
        });
      }
      triggerQuerySync({
        queryKeys: [
          curatorQueries.curations.detail({ curationId: id }).queryKey,
          curatorQueries.curations.myList._def,
        ],
      });
      queryClient.removeQueries({
        queryKey: curatorQueries.curations.myList._def,
      });
      snackbar.success('큐레이션이 변경되었습니다.');
      navigateBack();
    },
    onError: () => {
      snackbar.error('큐레이션이 수정에 실패했습니다.');
    },
  });

  const onSubmit = () => {
    const { title, description, selectedSnaps } = getValues();

    if (selectedSnaps.length === 0) {
      dialog.open({
        title: '스냅을 선택해야 큐레이션 등록이 가능합니다.',
        confirmButtonText: '확인',
        onConfirm: () => dialog.close(),
      });
      return;
    }

    updateCuration({
      curationId: curation.curationId,
      title,
      description: description ?? '',
      type: service === SERVICE['29CM'] ? 'GOODS' : 'SNAP2',
      goodsNoList: [],
      snapItems: selectedSnaps.map((s) => s.id),
    });
  };

  const watchTitle = watch('title');
  const watchDescription = watch('description');
  const watchSelectedSnaps = watch('selectedSnaps');

  const onOpenSnapProfile = () => {
    if (musinsaBaseUrl) {
      window.open(`${musinsaBaseUrl}/snap/profile`, '_blank', 'noopener,noreferrer');
    }
  };

  const handleClickSelectSnaps = () => {
    if (service === SERVICE.MUSINSA && curatorId) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'snap_add_try',
        component_name: 'button',
        event_name: 'click_button',
        from: getScreenName(),
        id: curatorId,
        section_name: 'curator_curation_create',
        type: 'snap',
      });
    }
    onClickSelectSnaps();
  };

  return (
    <form
      className={clsx('w-full grow flex flex-col bg-neutral-color-white', className)}
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex flex-col gap-1 px-4 pt-2 pb-3">
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <LabeledTextField
              label="제목 (필수)"
              maxLength={25}
              error={errors.title?.message}
              onClear={() => setValue('title', '')}
              count={watchTitle.length}
              placeholder="간절기 아우터 추천"
              {...field}
            />
          )}
        />
        <LabeledTextField
          label="설명 (선택)"
          maxLength={18}
          onClear={() => setValue('description', '')}
          count={watchDescription?.length}
          placeholder="50% 할인 상품 모음"
          {...register('description')}
        />
      </div>

      <div className="py-[12px]! px-[16px]!">
        <Divider variant="line" colorScheme="gray-500" />
      </div>

      <div className="flex flex-col grow z-[0] pb-[72px]!">
        {snapPoolQuery.isPending && (
          <div className="flex justify-center py-12">
            <Typography colorScheme="gray-600" variant="body13pxReg">
              스냅 목록 확인 중…
            </Typography>
          </div>
        )}

        {!snapPoolQuery.isPending && !hasSnapPool && snapPoolQuery.isFetched && (
          <div className="flex flex-col items-center gap-3 px-4! py-[87px]!">
            <div className="flex flex-col gap-0.5 text-center">
              <Typography variant="body14pxMed">상품이 태그된 스냅이 없습니다.</Typography>
              <Typography colorScheme="gray-600" variant="body13pxReg">
                스냅을 등록해보세요.
              </Typography>
            </div>
            <Button type="button" variant="secondary" size={24} onClick={onOpenSnapProfile}>
              <Typography variant="body13pxMed">스냅 가기</Typography>
            </Button>
          </div>
        )}

        {!snapPoolQuery.isPending && hasSnapPool && watchSelectedSnaps.length === 0 && (
          <>
            <div className="px-[16px]! py-[12px]! flex justify-between items-center">
              <Typography variant="body14pxMed">
                큐레이션 스냅
                {watchSelectedSnaps.length > 0 ? ` (${watchSelectedSnaps.length}개)` : ''}
              </Typography>
            </div>
            <div className="flex justify-center px-4 pb-3">
              <Button
                type="button"
                variant="secondary"
                size={40}
                className="w-full"
                onClick={handleClickSelectSnaps}
              >
                <Typography variant="body14pxMed">스냅 선택</Typography>
              </Button>
            </div>
          </>
        )}

        {!snapPoolQuery.isPending && hasSnapPool && watchSelectedSnaps.length > 0 && (
          <>
            <div className="px-[16px]! py-[12px]! flex justify-between items-center">
              <Typography variant="body14pxMed">
                큐레이션 스냅
                {watchSelectedSnaps.length > 0 ? ` (${watchSelectedSnaps.length}개)` : ''}
              </Typography>
            </div>
            <div className="flex justify-center px-4 pb-3">
              <Button
                type="button"
                variant="secondary"
                size={40}
                className="w-full"
                onClick={handleClickSelectSnaps}
              >
                <Typography variant="body14pxMed">스냅 선택</Typography>
              </Button>
            </div>
            <div className="grid w-full grid-cols-3 gap-0 px-0 pb-[69px] bg-neutral-color-white">
              {watchSelectedSnaps.map((snap) => (
                <div key={snap.id} className="relative aspect-[5/6] w-full">
                  {snap.imageUrl ? (
                    <Image src={snap.imageUrl} alt="" className="size-full object-cover" />
                  ) : (
                    <div className="size-full bg-gray-200" />
                  )}
                  <IconButton
                    type="button"
                    size="size20x20"
                    className="absolute right-2 top-2 z-10"
                    onClick={() => {
                      setValue(
                        'selectedSnaps',
                        watchSelectedSnaps.filter((s) => s.id !== snap.id),
                      );
                    }}
                  >
                    <IcCloseCircle />
                  </IconButton>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <BottomButtonWrapper>
        <FloatingButtonGroup.Container
          className="absolute -top-12 right-0"
          style={{
            transform: 'translateY(-100%)',
          }}
        >
          <ScrollToTopButton />
        </FloatingButtonGroup.Container>
        <Padding y="3">
          <Button type="submit" size="44" className="w-full">
            <Typography size="14" weight="medium">
              수정하기
            </Typography>
          </Button>
        </Padding>
      </BottomButtonWrapper>
    </form>
  );
}
